import React, { Fragment, useEffect, useMemo, useState } from "react";
import { buildKycDashboardReturnLink, kycBreadcrumbLabelsMatch } from "./kycHelpers";
import "./KYC-PrimaryContact.css";
import { Breadcrumb, Button, Card, Spin } from "antd";
import {
	ApartmentOutlined,
	DoubleLeftOutlined,
	EnvironmentOutlined,
	GlobalOutlined,
	MailOutlined,
	PhoneOutlined,
	PushpinOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";

const getValue = (obj, keys) => {
	for (const key of keys) {
		const value = obj?.[key];
		if (
			value !== undefined &&
			value !== null &&
			value !== "" &&
			value !== "Microsoft.Xrm.Sdk.OptionSetValue"
		) {
			return value;
		}
	}

	return "-";
};

const buildAddressLine = (data) => {
	const parts = [
		getValue(data, ["address1_Line1", "address1_line1", "address1"]),
		getValue(data, ["address1_Line2", "address1_line2", "address2"]),
		getValue(data, ["address1_Line3", "address1_county", "area", "areaName"]),
		getValue(data, ["ispl_City", "address1_city", "city"]),
	]
		.filter((part) => part && part !== "-");

	return parts.length ? parts.join(", ") : "-";
};

const KycPrimaryContactDetails = () => {
	const { sapCode } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [primaryContactData, setPrimaryContactData] = useState({});

	const groupCompanyId = location.state?.groupCompanyId || "";
	const groupCompanyBreadcrumbLabel = location.state?.groupCompanyName || "Group Company";
	const accountBreadcrumbLabel = location.state?.accountName || "Account";

	const returnTab = useMemo(() => {
		if (location.state?.returnTab) {
			return location.state.returnTab;
		}
		if (location.state?.fromAccountTab) {
			return "account";
		}
		if (location.state?.fromGroupCompanyTab) {
			return "groupCompany";
		}
		const hasAccount =
			accountBreadcrumbLabel !== "Account" && String(accountBreadcrumbLabel).trim() !== "";
		return hasAccount ? "account" : "groupCompany";
	}, [
		location.state?.returnTab,
		location.state?.fromAccountTab,
		location.state?.fromGroupCompanyTab,
		accountBreadcrumbLabel,
	]);

	const openedFromAccountTab = returnTab === "account";

	const hasGroupBreadcrumb =
		(groupCompanyBreadcrumbLabel !== "Group Company" &&
			String(groupCompanyBreadcrumbLabel).trim() !== "") ||
		Boolean(groupCompanyId && String(groupCompanyId).trim());
	const hasAccountBreadcrumb =
		openedFromAccountTab &&
		accountBreadcrumbLabel !== "Account" &&
		String(accountBreadcrumbLabel).trim() !== "";

	const groupAndAccountNamesMatch = kycBreadcrumbLabelsMatch(
		groupCompanyBreadcrumbLabel !== "Group Company" ? groupCompanyBreadcrumbLabel : "",
		hasAccountBreadcrumb ? accountBreadcrumbLabel : ""
	);

	const showGroupInBreadcrumb =
		hasGroupBreadcrumb && (!groupAndAccountNamesMatch || !openedFromAccountTab);
	const showAccountInBreadcrumb =
		hasAccountBreadcrumb && (!groupAndAccountNamesMatch || openedFromAccountTab);

	const dashboardGroupCompanyLink = useMemo(() => {
		if (!hasGroupBreadcrumb) {
			return "/kyc";
		}

		return buildKycDashboardReturnLink({
			returnTab: "groupCompany",
			groupCompanyId,
			groupCompanyName:
				groupCompanyBreadcrumbLabel !== "Group Company" ? groupCompanyBreadcrumbLabel : "",
		});
	}, [groupCompanyId, groupCompanyBreadcrumbLabel, hasGroupBreadcrumb]);

	const dashboardAccountLink = useMemo(() => {
		if (!hasAccountBreadcrumb) {
			return "/kyc?companyTab=account";
		}

		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId,
			groupCompanyName:
				groupCompanyBreadcrumbLabel !== "Group Company" ? groupCompanyBreadcrumbLabel : "",
			accountName: accountBreadcrumbLabel,
		});
	}, [
		hasAccountBreadcrumb,
		groupCompanyId,
		groupCompanyBreadcrumbLabel,
		accountBreadcrumbLabel,
	]);

	useEffect(() => {
		if (!sapCode) {
			return;
		}

		setIsLoading(true);
		KycService.getPrimaryContactDataBySapCodeAPI(sapCode)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData;
					const finalData = Array.isArray(responseData) ? responseData[0] || {} : responseData || {};
					setPrimaryContactData(finalData);
				} else {
					NotificationManager.error(response.data?.message || "Unable to load primary contact details.");
					setPrimaryContactData({});
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load primary contact details.");
				setPrimaryContactData({});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [sapCode]);

	const contactDisplay = useMemo(() => {
		const firstName = getValue(primaryContactData, [
			"firstName",
			"firstname",
			"ispl_FirstName",
		]);
		const lastName = getValue(primaryContactData, [
			"lastName",
			"lastname",
			"ispl_LastName",
		]);
		const fullName =
			firstName !== "-" && lastName !== "-"
				? `${firstName} ${lastName}`.trim()
				: firstName !== "-"
					? firstName
					: lastName !== "-"
						? lastName
						: "-";

		const avatarInitial =
			fullName !== "-"
				? fullName.trim().charAt(0).toUpperCase()
				: "?";

		return {
			displayName: fullName !== "-" ? fullName.toUpperCase() : "-",
			avatarInitial,
			email: getValue(primaryContactData, ["email", "emailaddress1", "primaryEmail"]),
			phone: getValue(primaryContactData, [
				"mobilePhone",
				"mobilephone",
				"telephone1",
				"phone",
			]),
			addressLine: buildAddressLine(primaryContactData),
			geoItems: [
				{
					key: "country",
					label: "Country",
					value: getValue(primaryContactData, [
						"ispl_Country",
						"address1_country",
						"country",
						"countryName",
					]),
					icon: <GlobalOutlined />,
				},
				{
					key: "area",
					label: "Area",
					value: getValue(primaryContactData, [
						"address1_Line3",
						"address1_county",
						"area",
						"areaName",
						"ispl_Area",
					]),
					icon: <PushpinOutlined />,
				},
				{
					key: "state",
					label: "State",
					value: getValue(primaryContactData, [
						"ispl_State",
						"address1_stateorprovince",
						"state",
						"stateName",
					]),
					icon: <EnvironmentOutlined />,
				},
				{
					key: "postalCode",
					label: "Postal Code",
					value: getValue(primaryContactData, [
						"ispl_PostCode",
						"address1_postalcode",
						"postalCode",
						"zipcode",
					]),
					icon: <ApartmentOutlined />,
				},
			],
		};
	}, [primaryContactData]);

	const primaryContactBreadcrumbLabel = useMemo(() => {
		const stateName = location.state?.primaryContactName;
		if (stateName !== undefined && stateName !== null && String(stateName).trim() !== "") {
			return String(stateName).trim();
		}
		if (contactDisplay.displayName && contactDisplay.displayName !== "-") {
			return contactDisplay.displayName;
		}
		return "Primary Contact";
	}, [location.state?.primaryContactName, contactDisplay.displayName]);

	const dashboardBackLink = useMemo(() => {
		if (openedFromAccountTab && hasAccountBreadcrumb) {
			return buildKycDashboardReturnLink({
				returnTab: "account",
				groupCompanyId,
				groupCompanyName:
					groupCompanyBreadcrumbLabel !== "Group Company" ? groupCompanyBreadcrumbLabel : "",
				accountName: accountBreadcrumbLabel,
			});
		}

		if (hasGroupBreadcrumb) {
			return buildKycDashboardReturnLink({
				returnTab: "groupCompany",
				groupCompanyId,
				groupCompanyName:
					groupCompanyBreadcrumbLabel !== "Group Company" ? groupCompanyBreadcrumbLabel : "",
			});
		}

		return "/kyc";
	}, [
		openedFromAccountTab,
		hasAccountBreadcrumb,
		hasGroupBreadcrumb,
		groupCompanyId,
		groupCompanyBreadcrumbLabel,
		accountBreadcrumbLabel,
	]);

	return (
		<Fragment>
			<div className="dashboard app-content content">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{showGroupInBreadcrumb ? (
									<Breadcrumb.Item>
										<Link to={dashboardGroupCompanyLink}>
											<label>{groupCompanyBreadcrumbLabel}</label>
										</Link>
									</Breadcrumb.Item>
								) : null}
								{showAccountInBreadcrumb ? (
									<Breadcrumb.Item>
										<Link to={dashboardAccountLink}>
											<label>{accountBreadcrumbLabel}</label>
										</Link>
									</Breadcrumb.Item>
								) : null}
								<Breadcrumb.Item>
									<label>{primaryContactBreadcrumbLabel}</label>
								</Breadcrumb.Item>
							</Breadcrumb>
							<Link to={dashboardBackLink}>
								<Button type="primary back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>
						<Spin size="large" spinning={isLoading}>
							<Card className="kyc-primary-details-card">
								<div className="kyc-primary-contact-panel">
									<div className="kyc-primary-contact-panel__head">
										<span className="kyc-primary-contact-panel__head-icon" aria-hidden>
											<UserOutlined />
										</span>
										<h3>Primary Contact Details</h3>
									</div>

									<div className="kyc-primary-contact-profile">
										<div
											className="kyc-primary-contact-avatar"
											aria-hidden={contactDisplay.displayName === "-"}
										>
											{contactDisplay.avatarInitial}
										</div>
										<div className="kyc-primary-contact-profile__body">
											<h4>{contactDisplay.displayName}</h4>
											<div className="kyc-primary-contact-chips">
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--email"
														aria-hidden
													>
														<MailOutlined />
													</span>
													{contactDisplay.email !== "-" ? (
														<a
															className="kyc-primary-contact-chip__text"
															href={`mailto:${contactDisplay.email}`}
														>
															{contactDisplay.email}
														</a>
													) : (
														<span className="kyc-primary-contact-chip__text">
															{contactDisplay.email}
														</span>
													)}
												</div>
												<div className="kyc-primary-contact-chip">
													<span
														className="kyc-primary-contact-chip__icon kyc-primary-contact-chip__icon--phone"
														aria-hidden
													>
														<PhoneOutlined />
													</span>
													{contactDisplay.phone !== "-" ? (
														<a
															className="kyc-primary-contact-chip__text"
															href={`tel:${contactDisplay.phone}`}
														>
															{contactDisplay.phone}
														</a>
													) : (
														<span className="kyc-primary-contact-chip__text">
															{contactDisplay.phone}
														</span>
													)}
												</div>
											</div>
										</div>
									</div>

									<div className="kyc-primary-contact-section">
										<div className="kyc-primary-contact-section__label">
											<span
												className="kyc-primary-contact-address__icon"
												aria-hidden
											>
												<EnvironmentOutlined />
											</span>
											Address
										</div>
										<div className="kyc-primary-contact-address">
											<span className="kyc-primary-contact-address__icon" aria-hidden>
												<ApartmentOutlined />
											</span>
											<p>{contactDisplay.addressLine}</p>
										</div>
									</div>

									<div className="kyc-primary-contact-geo">
										{contactDisplay.geoItems.map((item) => (
											<div className="kyc-primary-contact-geo-item" key={item.key}>
												<span className="kyc-primary-contact-geo-item__icon" aria-hidden>
													{item.icon}
												</span>
												<div className="kyc-primary-contact-geo-item__content">
													<label>{item.label}</label>
													<p>{item.value}</p>
												</div>
											</div>
										))}
									</div>
								</div>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycPrimaryContactDetails;
